@if (loading) {
  <div class="loading-container">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading dashboard...</p>
  </div>
} @else {
  <div class="dashboard-page">
  <div class="dashboard-main-well">
  <!-- Welcome Section -->
  <div class="welcome-section">
    <div class="container-fluid px-0">
      <div class="row">
        <div class="col-12">
          <h1 class="welcome-title">
            <span class="welcome-greeting">Welcome Back,</span>
            <span class="welcome-name">{{ doctorName }}</span>
          </h1>
          <p class="welcome-subtitle">Your medical transcription dashboard for neurological practice</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Search Interface -->
  <div class="search-section">
    <div class="container-fluid px-0">
      <div class="row">
        <div class="col-12">
          <div class="search-container">
            <!-- Search Input Field -->
            <div class="col-md-6 col-lg-8">
              <div class="search-input-wrapper">
                <span class="p-input-icon-left">
                  <input 
                    pInputText 
                    type="text" 
                    [(ngModel)]="searchText"
                    placeholder="Search by patient name, MRN, or transcription status..."
                    class="search-input"
                    autofocus="false"
                    (keyup.enter)="onSearch()"
                  />
                </span>
              </div>
            </div>

            <!-- Status Dropdown -->
            <div class="col-md-3 col-lg-2">
              <div class="status-dropdown-wrapper">
                <p-select 
                  [options]="statusOptions" 
                  [(ngModel)]="selectedStatus"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="All Status"
                  class="status-dropdown"
                  autofocus="false">
                </p-select>
              </div>
            </div>

            <!-- Search Button -->
            <div class="col-md-3 col-lg-2">
              <div class="search-button-wrapper">
                <p-button 
                  label="Search" 
                  icon="pi pi-search" 
                  (onClick)="onSearch()"
                  severity="primary"
                  class="search-button">
                </p-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Key Metrics Section (inside @else so loading is false; full-page spinner covers loading) -->
  <div class="metrics-section">
    <div class="container-fluid px-0">
      <div class="row">
        <!-- Error State -->
        <div *ngIf="error" class="col-12 text-center">
          <p>Error loading metrics. Please try again.</p>
        </div>

        <!-- No data (successful load, no transcriptions) -->
        <div *ngIf="!loading && !error && transcriptions.length === 0" class="col-12 text-center">
          <p>No statistics exist yet.</p>
        </div>
        
        <!-- Key Metrics Cards -->
        <div *ngIf="shouldShowContent" class="col-lg-4 col-md-12 mb-4">
          <div class="metric-card needs-review-card">
            <div class="card-content">
              <div class="card-header">
                <h3 class="card-title">Needs Review</h3>
                <div class="card-icon needs-review-icon">
                  <i class="pi pi-file"></i>
                </div>
              </div>
              <div class="card-body">
                <div class="metric-number">{{ keyMetricsMap['needs-review'] || '-' }}</div>
                <div class="metric-description">SOAP notes awaiting review before EMR submission</div>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="shouldShowContent" class="col-lg-4 col-md-12 mb-4">
          <div class="metric-card in-progress-card">
            <div class="card-content">
              <div class="card-header">
                <h3 class="card-title">In Progress</h3>
                <div class="card-icon in-progress-icon">
                  <i class="pi pi-microphone"></i>
                </div>
              </div>
              <div class="card-body">
                <div class="metric-number">{{ keyMetricsMap['in-progress'] || '-' }}</div>
                <div class="metric-description">Transcriptions currently being processed</div>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="shouldShowContent" class="col-lg-4 col-md-12 mb-4">
          <div class="metric-card completed-card">
            <div class="card-content">
              <div class="card-header">
                <h3 class="card-title">Completed</h3>
                <div class="card-icon completed-icon">
                  <i class="pi pi-check-circle"></i>
                </div>
              </div>
              <div class="card-body">
                <div class="metric-number">{{ keyMetricsMap['completed'] || '-' }}</div>
                <div class="metric-description">Transcriptions already completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Summary Statistics Section (inside @else so loading is false; full-page spinner covers loading) -->
  <div class="summary-section">
    <div class="container-fluid px-0">
      <div class="row">
        <!-- Error State -->
        <div *ngIf="error" class="col-12 text-center">
          <p>Error loading summary statistics. Please try again.</p>
        </div>

        <!-- No data (successful load, no transcriptions) - CTA to record -->
        <div *ngIf="!loading && !error && transcriptions.length === 0" class="col-12 text-center">
          <p><a routerLink="/v0/recording" class="record-cta">Record a conversation</a></p>
        </div>
        
        <!-- Summary Statistics Cards -->
        <div *ngIf="shouldShowContent" class="col-lg-3 col-md-6 col-sm-6 mb-4">
          <div class="summary-card">
            <div class="summary-content">
              <div class="summary-label">Total Transcriptions</div>
              <div class="summary-number">{{ summaryStatisticsMap['total-transcriptions'] }}</div>
            </div>
            <div class="summary-icon">
              <i class="pi pi-file"></i>
            </div>
          </div>
        </div>

        <div *ngIf="shouldShowContent" class="col-lg-3 col-md-6 col-sm-6 mb-4">
          <div class="summary-card">
            <div class="summary-content">
              <div class="summary-label">Patients</div>
              <div class="summary-number">{{ summaryStatisticsMap['patients'] || 0 }}</div>
            </div>
            <div class="summary-icon">
              <i class="pi pi-users"></i>
            </div>
          </div>
        </div>

        <div *ngIf="shouldShowContent" class="col-lg-3 col-md-6 col-sm-6 mb-4">
          <div class="summary-card">
            <div class="summary-content">
              <div class="summary-label">Hours Recorded</div>
              <div class="summary-number">{{ summaryStatisticsMap['hours-recorded'] || 0 }}</div>
            </div>
            <div class="summary-icon">
              <i class="pi pi-microphone"></i>
            </div>
          </div>
        </div>

        <div *ngIf="shouldShowContent" class="col-lg-3 col-md-6 col-sm-6 mb-4">
          <div class="summary-card">
            <div class="summary-content">
              <div class="summary-label">Completion Rate</div>
              <div class="summary-number">{{ summaryStatisticsMap['accuracy-rate'] || 0 }}%</div>
            </div>
            <div class="summary-icon">
              <i class="pi pi-check-circle"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Recent Transcriptions Section -->
  <div class="transcriptions-section">
    <div class="container-fluid px-0">
      <h2 class="transcriptions-title">Recent Transcriptions</h2>
      
      <div class="transcription-cards">
        <div *ngIf="transcriptions.length === 0" class="no-transcriptions">
          <p>No Transcriptions found.</p>
        </div>
        <div
          *ngFor="let transcription of transcriptions | slice:0:3"
          class="transcription-card"
          [class.transcription-card--locked]="isTranscriptionInProgress(transcription)">
          <div class="card-header">
            <h3 class="transcription-title">{{ getTranscriptionTitle(transcription) }}</h3>
            <p-button
              [label]="recentTranscriptionButtonLabel(transcription)"
              [outlined]="true"
              size="small"
              class="view-button"
              [disabled]="isTranscriptionInProgress(transcription)"
              (onClick)="onRecentTranscriptionAction(transcription)"></p-button>
          </div>
          <div class="card-body">
            <div class="patient-info">
              <span>Patient: </span><a href="#" class="patient-name">{{ transcription.patientName || 'Unknown' }}</a><span class="patient-mrn"> (MRN: {{ transcription.mrn || 'N/A' }})</span>
            </div>
            <div class="transcription-meta">
              <div class="meta-item">
                <span class="meta-label col-2">Date:</span>
                <span class="date col-8">{{ formatDate(transcription.date) }}</span>
                <span><p-tag [value]="getDisplayStatus(transcription.status)" [severity]="getStatusSeverity(transcription.status)" class="status-tag"></p-tag></span>
              </div>
              <div class="meta-item">
                <span class="meta-label col-2">Duration:</span>
                <span class="duration col-8">{{ transcription.duration || 'N/A' }}</span>
              </div>
            </div>
          </div>
          <div class="card-footer">
            
          </div>
        </div>


      </div>

      <div *ngIf="transcriptions.length > 0" class="view-all-container">
        <p-button
          label="View All Transcriptions"
          [outlined]="true"
          class="view-all-button"
          (onClick)="onViewAllTranscriptions()"></p-button>
      </div>

    </div>
  </div>

  <!-- Dashboard Content -->
  <div class="dashboard-content">
    <div class="container-fluid px-0">
      <div class="row">
        <div class="col-12">
          <!-- Demo Component -->
          <!-- <app-demo></app-demo> -->
        </div>
      </div>
    </div>
  </div>
  </div>
  </div>
}